/**
 * Blocks in three dimensions, drawn on a flat canvas.
 *
 * There is no 3-D engine here and there does not need to be one: an isometric
 * grid is a fixed piece of arithmetic that turns (across, up, back) into (x, y)
 * on the paper. Every cube is three parallelograms — a top, a left and a right
 * — and the whole illusion holds together as long as two things are right:
 * the projection, and the ORDER the cubes are drawn in. Get the order wrong and
 * blocks at the back are painted over the ones in front, which reads as the
 * model being inside out.
 *
 * Turning the model is the point, not a feature: seeing that the same thing
 * looks different from another side, and is still the same thing, is the whole
 * beginning of thinking in three dimensions.
 */

export interface Cell {
  x: number;
  /** Up. Zero is the ground. */
  y: number;
  z: number;
}

export interface Block extends Cell {
  color: string;
}

/** A model is blocks by position, so a cell can only ever hold one. */
export type Model = Map<string, Block>;

export const GROUND = 0;
/** How far the grid reaches from the middle, in blocks. */
export const REACH = 7;
export const MAX_HEIGHT = 9;

export function keyOf(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

export function parseKey(key: string): Cell | null {
  const parts = key.split(',').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return { x: parts[0], y: parts[1], z: parts[2] };
}

export function inBounds(cell: Cell): boolean {
  return (
    Math.abs(cell.x) <= REACH &&
    Math.abs(cell.z) <= REACH &&
    cell.y >= GROUND &&
    cell.y < MAX_HEIGHT
  );
}

export function addBlock(model: Model, block: Block): Model {
  if (!inBounds(block)) return model;
  const next = new Map(model);
  next.set(keyOf(block.x, block.y, block.z), block);
  return next;
}

export function removeBlock(model: Model, cell: Cell): Model {
  const key = keyOf(cell.x, cell.y, cell.z);
  if (!model.has(key)) return model;
  const next = new Map(model);
  next.delete(key);
  return next;
}

export function blockAt(model: Model, cell: Cell): Block | undefined {
  return model.get(keyOf(cell.x, cell.y, cell.z));
}

/* ------------------------------------------------------------- turning it */

export type Rotation = 0 | 1 | 2 | 3;

/**
 * Negating zero gives -0, which is a different value from 0 to anything that
 * compares strictly — including the keys a model is stored under. Turned
 * coordinates are normalised so a block at the middle stays one block.
 */
const zeroed = (n: number): number => (n === 0 ? 0 : n);

/** A quarter turn about the upright axis. Four of them get you back. */
export function turn(x: number, z: number, rotation: Rotation): { x: number; z: number } {
  switch (((rotation % 4) + 4) % 4) {
    case 1:
      return { x: zeroed(-z), z: zeroed(x) };
    case 2:
      return { x: zeroed(-x), z: zeroed(-z) };
    case 3:
      return { x: zeroed(z), z: zeroed(-x) };
    default:
      return { x: zeroed(x), z: zeroed(z) };
  }
}

export function unturn(x: number, z: number, rotation: Rotation): { x: number; z: number } {
  return turn(x, z, ((4 - (rotation % 4)) % 4) as Rotation);
}

/* ------------------------------------------------------------ projecting */

export interface Tile {
  /** Half the width of a block's top face. */
  halfW: number;
  /** Half the depth of a block's top face. */
  halfD: number;
  /** How tall a block stands. */
  rise: number;
}

/** Where the centre of a cell's top face lands on the paper. */
export function project(cell: Cell, rotation: Rotation, tile: Tile): { x: number; y: number } {
  const t = turn(cell.x, cell.z, rotation);
  return {
    x: (t.x - t.z) * tile.halfW,
    y: (t.x + t.z) * tile.halfD - cell.y * tile.rise,
  };
}

/**
 * The same projection at any angle, not just the four square ones — so the
 * model can be turned by dragging a finger rather than only in quarter steps.
 *
 * `angle` is in radians about the upright axis. At multiples of a right angle
 * this agrees exactly with `project`, which is what lets the two live side by
 * side: dragging turns freely, and letting go can settle onto a square face.
 */
export function projectFree(cell: Cell, angle: number, tile: Tile): { x: number; y: number } {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const rx = cell.x * cos - cell.z * sin;
  const rz = cell.x * sin + cell.z * cos;
  return {
    x: (rx - rz) * tile.halfW,
    y: (rx + rz) * tile.halfD - cell.y * tile.rise,
  };
}

/** Draw order for a freely-turned model: far blocks first, then lower ones. */
export function drawOrderFree(blocks: Block[], angle: number): Block[] {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const depth = (b: Block) => b.x * cos - b.z * sin + (b.x * sin + b.z * cos);
  return [...blocks].sort((a, b) => depth(a) - depth(b) || a.y - b.y);
}

/**
 * Which ground cell a point is over when the model has been turned freely —
 * the inverse of `projectFree` at height zero. Without this, tapping the
 * ground would put the block where it WOULD have gone at the last square
 * angle, which is somewhere else entirely.
 */
export function groundCellFree(px: number, py: number, angle: number, tile: Tile): Cell {
  const a = px / tile.halfW;
  const b = py / tile.halfD;
  const rx = (a + b) / 2;
  const rz = (b - a) / 2;
  // Un-rotate by the same angle.
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  // Rounding a hair below zero gives -0, which compares unequal to 0.
  return {
    x: zeroed(Math.round(rx * cos - rz * sin)),
    y: GROUND,
    z: zeroed(Math.round(rx * sin + rz * cos)),
  };
}

/** The nearest square-on angle, for settling after a drag. */
export function nearestRotation(angle: number): Rotation {
  const quarter = Math.PI / 2;
  return ((((Math.round(angle / quarter) % 4) + 4) % 4) as Rotation);
}

/**
 * Which ground cell a point on the paper is over — the inverse of `project`
 * at height zero, so tapping bare ground puts a block where you tapped.
 */
export function groundCell(px: number, py: number, rotation: Rotation, tile: Tile): Cell {
  const a = px / tile.halfW;
  const b = py / tile.halfD;
  const x = Math.round((a + b) / 2);
  const z = Math.round((b - a) / 2);
  const back = unturn(x, z, rotation);
  return { x: zeroed(back.x), y: GROUND, z: zeroed(back.z) };
}

/**
 * Far blocks first, and lower blocks before the ones stacked on them.
 *
 * Sorting by depth alone is not enough: two blocks in the same column are at
 * the same depth, and the upper one must be painted second or it disappears
 * behind its own neighbour.
 */
export function drawOrder(blocks: Block[], rotation: Rotation): Block[] {
  return [...blocks].sort((a, b) => {
    const ta = turn(a.x, a.z, rotation);
    const tb = turn(b.x, b.z, rotation);
    const depth = ta.x + ta.z - (tb.x + tb.z);
    return depth !== 0 ? depth : a.y - b.y;
  });
}

/* ---------------------------------------------------------------- reading */

export interface Extent {
  count: number;
  /** How many blocks across, deep and tall the model actually is. */
  width: number;
  depth: number;
  height: number;
}

export function extentOf(model: Model): Extent {
  const blocks = [...model.values()];
  if (blocks.length === 0) return { count: 0, width: 0, depth: 0, height: 0 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let maxY = -Infinity;
  for (const b of blocks) {
    minX = Math.min(minX, b.x);
    maxX = Math.max(maxX, b.x);
    minZ = Math.min(minZ, b.z);
    maxZ = Math.max(maxZ, b.z);
    maxY = Math.max(maxY, b.y);
  }
  return {
    count: blocks.length,
    width: maxX - minX + 1,
    depth: maxZ - minZ + 1,
    height: maxY - GROUND + 1,
  };
}

/** A block with nothing on top of it — where the next one would go. */
export function topOf(model: Model, x: number, z: number): number {
  let top = GROUND - 1;
  for (const block of model.values()) {
    if (block.x === x && block.z === z) top = Math.max(top, block.y);
  }
  return top;
}

/* ------------------------------------------------------------ remembering */

export interface ModelJson {
  v: 1;
  blocks: Array<[number, number, number, string]>;
}

export function encodeModel(model: Model): ModelJson {
  return { v: 1, blocks: [...model.values()].map((b) => [b.x, b.y, b.z, b.color]) };
}

export function decodeModel(raw: unknown): Model {
  const out: Model = new Map();
  if (typeof raw !== 'object' || raw === null) return out;
  const blocks = (raw as { blocks?: unknown }).blocks;
  if (!Array.isArray(blocks)) return out;
  for (const entry of blocks) {
    if (!Array.isArray(entry) || entry.length < 4) continue;
    const [x, y, z, color] = entry;
    if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') continue;
    if (typeof color !== 'string') continue;
    const block: Block = { x, y, z, color };
    if (inBounds(block)) out.set(keyOf(x, y, z), block);
  }
  return out;
}
